/* Live preview.
   The chart is drawn from the draft rather than from what is stored, so a range is checked
   while it is written and not only after Save. */

import {
  WRITTEN,
  applyNotes,
  presenceOf,
  spotFault,
  spotFromSpec,
  type Notes,
  type Spot,
  type SpotSpec,
  type WrittenAction,
} from "@preflop/core";
import { page, type CreatorDraft, type EditDraft } from "./state";

export const trimNotes = (n: Partial<Notes>): Notes => ({
  allin: (n.allin ?? "").trim(),
  raise: (n.raise ?? "").trim(),
  call: (n.call ?? "").trim(),
});
export const hasText = (n: Partial<Notes>): boolean => WRITTEN.some((a) => (n[a] ?? "").trim());
export const sameNotes = (a: Partial<Notes>, b: Partial<Notes>): boolean =>
  WRITTEN.every((k) => (a[k] ?? "").trim() === (b[k] ?? "").trim());

/** Why something failed, as one line. A spot refused on its way into storage fails with a
    list of issues; the first one is the line worth showing. */
export function messageOf(e: unknown): string {
  if (!(e instanceof Error)) return String(e);
  const issues: unknown = "issues" in e ? e.issues : null;
  const first: unknown = Array.isArray(issues) ? issues[0] : null;
  if (typeof first === "object" && first !== null && "message" in first) {
    const path = "path" in first && Array.isArray(first.path) ? first.path.join(".") : "";
    return `This spot cannot be stored: ${path ? path + ": " : ""}${String(first.message)}`;
  }
  return e.message;
}

export interface Preview {
  spot: Spot | null;
  error: string;
  warn: string[];
  /** Hands the notation names that hero does not hold here. A chart cannot be saved holding
      one: see `outsideFault`. */
  outside: string[];
}

/**
 * Why a draft naming hands hero cannot hold here cannot be saved.
 * A chart's frequencies are shares of the range hero arrives with, not of the deck, so a
 * hand that is not in that range is not a lighter frequency — it is a hand hero is not
 * holding in this spot at all, and a chart that names one is a chart of somewhere else.
 */
export function outsideFault(hands: readonly string[]): string {
  const one = hands.length === 1;
  const named = hands.length > 8 ? `${hands.length} hands` : hands.join(", ");
  return (
    `${named} ${one ? "is" : "are"} outside the range hero arrives with here, so this ` +
    `chart cannot be saved — take ${one ? "it" : "them"} out.`
  );
}

/**
 * The chart as the draft would store it, or the reason it cannot be built. Half-typed
 * notation throws constantly, so this reports instead of failing: the caller keeps the last
 * picture that did parse.
 */
export function previewOf(
  spot: Spot,
  notes: Partial<Notes>,
  priority?: WrittenAction | null,
): Preview {
  try {
    // The hands added are the difference between the range the chart started from and the
    // one it ends with: `applyNotes` joins a hand it did not anticipate at full presence.
    const before = presenceOf(spot);
    const c = structuredClone(spot);
    const warn = applyNotes(c, trimNotes(notes), priority);
    return { spot: c, error: "", warn, outside: c.baseInRange.filter((h) => !before.has(h)) };
  } catch (e) {
    return { spot: null, error: messageOf(e), warn: [], outside: [] };
  }
}

/** The draft open in the editor for this chart, when the editor is what is on screen. */
export const liveDraft = (spot: Spot): EditDraft | null => {
  const p = page();
  return p.editMode && !p.creator && p.editDraft?.key === spot.key ? p.editDraft : null;
};

/** The spot a creator draft describes, or null while it does not describe one yet. */
export function specOf(c: CreatorDraft): SpotSpec | null {
  const stack = c.stack.trim();
  const openSize = c.openSize.trim();
  if (!stack || !openSize || !c.hero || !c.action) return null;
  const seats = c.seats.filter((s): s is string => !!s);
  if (seats.length !== c.seats.length) return null;
  return {
    stakes: c.stakes,
    gameType: c.gameType,
    format: c.format,
    stack,
    openSize,
    hero: c.hero,
    action: c.action,
    seats,
    ...(c.branch ? { branch: c.branch } : {}),
    ...(c.profile ? { profile: c.profile } : {}),
    from: c.from,
    rangeNote: c.rangeNote,
    rangeFloor: c.rangeFloor,
  };
}

/**
 * The chart a creator draft starts from: the hands hero arrives with, before a frequency is
 * written into them. Null while the draft does not describe a spot yet, and on anything the
 * rules refuse — which `creatorPreview` reports and everyone else simply does without.
 */
export function creatorSpot(c: CreatorDraft): Spot | null {
  const spec = specOf(c);
  if (!spec || spotFault(c.gameType, c.hero, c.action, c.seats)) return null;
  return spotFromSpec(page().index, spec);
}

/** The chart the creator would produce, as far as its draft goes. */
function creatorPreview(c: CreatorDraft): Preview {
  const none: Preview = { spot: null, error: "", warn: [], outside: [] };
  let sp: Spot | null;
  try {
    sp = creatorSpot(c);
  } catch (e) {
    return { ...none, error: messageOf(e) };
  }
  return sp ? previewOf(sp, c.notes, c.priority) : none;
}

/** What the matrix draws: the draft as Save would leave it when one is open, the stored
 *  chart otherwise. The last picture that parsed is kept. */
export function chartSpot(spot: Spot): Spot {
  const { creator } = page();
  if (creator) {
    const pv = creatorPreview(creator);
    if (pv.spot) creator.preview = pv.spot;
    return creator.preview ?? spot;
  }
  const d = liveDraft(spot);
  if (!d) return spot;
  // Emptied fields are drawn, not skipped: what Save would leave behind is a chart that
  // folds every hand, and the matrix has to show that rather than the chart being replaced.
  const pv = previewOf(spot, d.notes, d.priority);
  d.error = pv.error;
  // A parse error leaves the last good picture up, and its note with it.
  if (pv.spot) {
    d.preview = pv.spot;
    d.warn = pv.warn.join(" · ");
    d.outside = pv.outside;
  }
  return d.preview ?? spot;
}

/* The editor's message line and its Save button, held together while the panel is up. A
   repaint has to reach both — the line says what the draft is, the button says whether it
   can be kept — and it cannot rebuild the panel to do it, because the caret is in it. */
let editMsg: HTMLElement | null = null;
let editSave: HTMLButtonElement | null = null;
export const setEditGuard = (msg: HTMLElement | null, save: HTMLButtonElement | null): void => {
  editMsg = msg;
  editSave = save;
};

/* Half-typed notation is not a mistake yet: the chart holds the last picture that parsed and
   the reason sits muted under the fields, because turning the panel red on the keystroke
   before the "s" of "AKs" would be shouting at someone mid-word. A hand hero cannot hold
   here is the other kind — a whole hand, named and wrong — so that one is red. Either way
   Save can do nothing with the draft, and goes off saying why. */
export function showDraftState(): void {
  // Only about what was typed. A stored chart that holds a hand over 100% cannot be parsed
  // back, and shouting about that before a key is pressed would blame the reader for it.
  const p = page();
  const d = p.dirty ? p.editDraft : null;
  const stop = d?.outside.length ? outsideFault(d.outside) : "";
  // Three empty fields store a chart that folds everything, which is a real answer and a
  // whole range at once. Said plainly, so it is never a wipe nobody asked for.
  const allFold =
    d && !hasText(d.notes) ? "Nothing is written, so hero folds every hand here." : "";
  // The refusal is the whole line: it names the same hands the notation's own warning does,
  // and what the notation quietly fixed is not worth reading past it.
  const text = stop || (d ? d.error || allFold || d.warn : "");
  if (editMsg) {
    editMsg.className = "msg " + (stop ? "err" : "soft");
    editMsg.textContent = text;
    editMsg.hidden = !text;
  }
  if (editSave) {
    const fault = stop || d?.error || "";
    editSave.disabled = !!fault;
    editSave.title = fault || "Keep these hands as this chart";
  }
}
